/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import type { ComponentProps, CSSProperties, Ref, RefCallback } from "react"
import { useMemo } from "react"

import { OverflowAxis, useIsOverflowing } from "../../utils/use-is-overflowing"

import styles from "./truncate.module.css"

const Kind = {
    None: "none",
    End: "end",
    Clamp: "clamp",
    Middle: "middle"
} as const

type CommonProps = Omit<ComponentProps<"span">, "children"> & {
    text: string
    /*
     * Shows the full text in a native tooltip when it is truncated.
     * A caller-provided `title` takes precedence.
     *
     * @default true
     */
    showTitle?: boolean
}

type NoneProps = CommonProps & {
    kind: typeof Kind.None
}
type EndProps = CommonProps & {
    /** @default "end" */
    kind?: typeof Kind.End
}
type ClampProps = CommonProps & {
    kind: typeof Kind.Clamp
    /**
     * Number of lines to keep before the text is cut.
     *
     * @default 2
     */
    lines?: number
}
type MiddleProps = CommonProps & {
    kind: typeof Kind.Middle
    /**
     * Minimum width reserved for the start of the text, as a CSS length.
     *
     * @default "4em"
     */
    start?: string
    /**
     * Minimum width reserved for the end of the text, as a CSS length.
     *
     * @default "4em"
     */
    end?: string
}

type Props = NoneProps | EndProps | ClampProps | MiddleProps

/**
 * Truncates text at the end, by line count, or in the middle while keeping
 * both ends visible.
 *
 * Use `kind="none"` to render the text without truncation, so callers can
 * switch the truncation mode without branching around the component.
 *
 * @example
 * <Truncate text={name} />
 * <Truncate kind={truncate ? "middle" : "none"} text={id} />
 * <Truncate kind="clamp" lines={3} text={description} />
 * <Truncate kind="middle" start="12ch" end="6ch" text={deviceId} />
 */
export const Truncate = (props: Props) => {
    if (props.kind === Kind.None) {
        return <NoneTruncate {...props} />
    }
    if (props.kind === Kind.Clamp) {
        return <ClampTruncate {...props} />
    }
    if (props.kind === Kind.Middle) {
        return <MiddleTruncate {...props} />
    }
    return <EndTruncate {...props} />
}

/*
 * Renders the text unchanged. Keeping it as a component lets callers switch on
 * `kind` without branching around the component when truncation is not needed.
 */
const NoneTruncate = ({ kind: _kind, showTitle: _showTitle, text, ...rest }: NoneProps) => <span {...rest}>{text}</span>

const EndTruncate = ({ kind: _kind, className, ref, showTitle = true, text, title, ...rest }: EndProps) => {
    const { contentRef, isOverflowing } = useIsOverflowing({ disabled: !showTitle, watch: text })
    const mergedRef = useMergedRef(ref, contentRef)

    return (
        <span
            {...rest}
            ref={mergedRef}
            data-slot="truncate"
            data-kind={Kind.End}
            className={clsx(styles.root, styles.end, className)}
            title={title ?? (showTitle && isOverflowing ? text : undefined)}
        >
            {text}
        </span>
    )
}

const ClampTruncate = ({
    kind: _kind,
    className,
    lines = 2,
    ref,
    showTitle = true,
    style,
    text,
    title,
    ...rest
}: ClampProps) => {
    const { contentRef, isOverflowing } = useIsOverflowing({
        axis: OverflowAxis.Block,
        disabled: !showTitle,
        watch: text
    })
    const mergedRef = useMergedRef(ref, contentRef)

    return (
        <span
            {...rest}
            ref={mergedRef}
            data-slot="truncate"
            data-kind={Kind.Clamp}
            className={clsx(styles.root, styles.clamp, className)}
            style={{ "--truncate-lines": lines, ...style } as CSSProperties}
            title={title ?? (showTitle && isOverflowing ? text : undefined)}
        >
            {text}
        </span>
    )
}

const MiddleTruncate = ({
    kind: _kind,
    className,
    end = "4em",
    ref,
    showTitle = true,
    start = "4em",
    style,
    text,
    title,
    ...rest
}: MiddleProps) => {
    const { containerRef, contentRef, isOverflowing } = useIsOverflowing({ watch: text })
    const mergedRef = useMergedRef(ref, containerRef)

    return (
        <span
            {...rest}
            ref={mergedRef}
            data-slot="truncate"
            data-kind={Kind.Middle}
            data-overflowing={isOverflowing || undefined}
            className={clsx(styles.root, styles.middle, className)}
            style={{ "--truncate-start": start, "--truncate-end": end, ...style } as CSSProperties}
            title={title ?? (showTitle && isOverflowing ? text : undefined)}
        >
            <span data-slot="truncate-start" className={styles.head} ref={contentRef}>
                {text}
            </span>
            {/*
             * The tail is laid out right-to-left so overflow clips its beginning. Wrap
             * the value in `bdi` so its own direction is detected independently; otherwise
             * trailing neutral characters such as `=` or `)` can be reordered by the
             * surrounding RTL paragraph.
             */}
            <span data-slot="truncate-end" className={styles.tail} aria-hidden="true">
                <bdi>{text}</bdi>
            </span>
        </span>
    )
}

/**
 * Feeds one element to both the caller's ref and the component's own ref.
 *
 * Memoised on purpose: a fresh callback on every render would detach and
 * reattach the ref, which hands the measuring hook a `null` element between
 * every pair of renders.
 *
 * @param external - Ref passed in by the caller. Callback refs and object refs both work.
 * @param internal - Ref the component needs for itself, always a callback.
 * @returns One stable callback ref that writes the element to both.
 *
 * @example
 * const mergedRef = useMergedRef(ref, contentRef)
 * return <span ref={mergedRef}>{text}</span>
 */
const useMergedRef = <T extends HTMLElement>(external: Ref<T> | undefined, internal: RefCallback<T>): RefCallback<T> =>
    useMemo(
        () => (element: T | null) => {
            internal(element)

            if (typeof external === "function") {
                external(element)
            } else if (external) {
                external.current = element
            }
        },
        [external, internal]
    )
